import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["aria-describedby", "aria-label", "aria-labelledby", "affordance", "error", "inputRef", "minWidth", "onBlur", "onClick", "onFocus", "onMouseEnter", "onMouseLeave", "onMouseMove", "readOnly", "use", "maxLength", "value", "focused", "hovered", "placeholder", "onChange", "onKeyDown", "onKeyUp", "disabled", "autoFocus", "data-test-id"],
  _excluded2 = ["hovered"],
  _excluded3 = ["focused"];
import unescapedText from 'I18n/utils/unescapedText';
import { useEffect, useState, useMemo, useRef, forwardRef, memo } from 'react';
import memoizeOne from 'react-utils/memoizeOne';
import styled from 'foundations-theming/utils';
import { callIfPossible } from '../../utils/functions';
import { Controllable } from '../Controllable';
import ShareInput from '../ShareInput/ShareInput';
import { FocusProvider } from '../../utils/FocusProvider';
import { HoverProvider } from '../../utils/useHovered';
import { getTextWidth } from '../../utils/MeasureText';
import { setUiTransition, getPlaceholderStyles } from './AutoSizedTextInput.helpers';
import { Text } from '../../../components/Text/Text';
import { ScreenReaderOnly } from '../ScreenReaderOnly/ScreenReaderOnly';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { specialtyColor } from 'foundations-theming-specialty/utils';
import { getFocusStyleObject } from '../../utils/focus/focus.style';
import { EditIcon } from 'foundations-assets/icons/components/EditIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const INPUT_MAX_LENGTH_WARNING_THRESHOLD = 15;
const getStyle = inputWidth => ({
  width: inputWidth
});
const placeholderStyles = color => {
  return getPlaceholderStyles({
    color
  });
};
const computeBackgroundColor = ({
  focused,
  hovered
}) => {
  if (!focused && !hovered) return 'transparent';
  return specialtyColor('global-shell-fill-surface-default');
};
export const getErrorObject = theme => ({
  borderColor: `${theme.color['border-alert-default']} !important`,
  boxShadow: `0 0 0 1px ${theme.color['border-alert-default']} !important`
});
const Wrapper = styled.div.withConfig({
  displayName: "AutoSizedTextInput__Wrapper",
  componentId: "sc-193lpaf-0"
})(props => {
  const theme = props.theme;
  return Object.assign({
    display: 'inline-block',
    position: 'relative',
    maxWidth: '100%',
    color: props.use === 'on-dark' ? theme.color['fill-surface-default'] : ''
  }, props.$cssStyles);
});
const Input = styled.input.withConfig({
  displayName: "AutoSizedTextInput__Input",
  componentId: "sc-193lpaf-1"
})(props => {
  const theme = props.theme;
  const color = props.use === 'on-dark' ? theme.color['border-core-subtle-hover'] : theme.color['border-core-hover'];
  return Object.assign({
    backgroundColor: theme.color['fill-transparent-default'],
    border: 0,
    boxSizing: 'content-box',
    color: 'inherit',
    cursor: 'text',
    maxWidth: '100%',
    outline: props.affordance ? 0 : undefined,
    font: 'inherit',
    '&:not(:empty)': {
      display: 'inline-block'
    }
  }, placeholderStyles(color), props.$width);
});
export const Affordance = styled.div.withConfig({
  displayName: "AutoSizedTextInput__Affordance",
  componentId: "sc-193lpaf-2"
})(({
  show,
  focused,
  hovered,
  error,
  theme
}) => {
  const errorObject = getErrorObject(theme);
  if (show) {
    return Object.assign({
      display: 'inline-block',
      margin: `-${theme.space['100']} ${theme.space['0']} -${theme.space['100']} -${theme.space['100']}`,
      maxWidth: `calc(100% + ${theme.space['100']})`,
      // space token usage is valid since the purpose is to compensate for the negative margin
      padding: `${theme.space['100']} 38px ${theme.space['100']} ${theme.space['100']}`,
      // no token for 38px
      backgroundColor: computeBackgroundColor({
        focused,
        hovered
      }),
      borderColor: 'currentColor'
    }, focused && getFocusStyleObject({
      theme,
      unwrapped: true
    }), error && errorObject);
  }
  return {};
});
export const StyledIconWrapper = styled.div.withConfig({
  displayName: "AutoSizedTextInput__StyledIconWrapper",
  componentId: "sc-193lpaf-3"
})(props => {
  const theme = props.theme;

  // Determine the appropriate color based on state and use
  const getIconColor = () => {
    if (props.use === 'on-dark') {
      if (props.focused) return theme.color['icon-interactive-on-fill-pressed'];
      if (props.hovered) return theme.color['icon-interactive-on-fill-hover'];
      return theme.color['icon-interactive-on-fill-default'];
    } else {
      if (props.focused) return theme.color['icon-interactive-pressed'];
      if (props.hovered) return theme.color['icon-interactive-hover'];
      return theme.color['icon-interactive-default'];
    }
  };
  return Object.assign({
    blockSize: '1em',
    // ensure the edit icon is vertically centered
    position: 'absolute',
    top: '50%',
    right: 10,
    transform: 'translateY(-50%)',
    fontSize: theme.typography['heading-100'].fontSize,
    color: getIconColor(),
    pointerEvents: 'none',
    opacity: props.focused ? 0 : 1
  }, setUiTransition());
});
const getCharacterCounterOpacity = ({
  focused,
  maxLength,
  value = ''
}) => focused && maxLength && maxLength - value.length <= INPUT_MAX_LENGTH_WARNING_THRESHOLD ? 1 : 0;
const StyledCharacterCounter = styled.div.withConfig({
  displayName: "AutoSizedTextInput__StyledCharacterCounter",
  componentId: "sc-193lpaf-4"
})(props => {
  const theme = props.theme;
  const {
    $focused,
    $maxLength,
    $value
  } = props;
  return Object.assign({
    position: 'absolute',
    top: '50%',
    right: theme.space['300'],
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    opacity: getCharacterCounterOpacity({
      focused: $focused,
      maxLength: $maxLength,
      value: $value
    })
  }, setUiTransition(), theme.typography['body-200']);
});
const CharacterCounter = props => {
  const {
    ariaLiveRegionId,
    maxLength,
    value = '',
    focused,
    use
  } = props;
  if (!maxLength) return null;
  const charactersRemaining = maxLength - value.length;

  // Render hidden text when approaching the threshold
  if (charactersRemaining > INPUT_MAX_LENGTH_WARNING_THRESHOLD + 1) {
    return null;
  }
  const displayedCharactersRemaining = charactersRemaining > INPUT_MAX_LENGTH_WARNING_THRESHOLD ? INPUT_MAX_LENGTH_WARNING_THRESHOLD : charactersRemaining;
  return /*#__PURE__*/_jsxs(StyledCharacterCounter, {
    $focused: focused,
    $value: value,
    $maxLength: maxLength,
    children: [/*#__PURE__*/_jsx("span", {
      "aria-hidden": true,
      children: displayedCharactersRemaining
    }), /*#__PURE__*/_jsx(Text, {
      typography: "body-200",
      color:
      // TODO: missing token. fill-tertiary-pressed doesn't make sense for a text token here, but we don't have a text token available that works
      // issue link:https://git.hubteam.com/HubSpot/foundations-components/pull/6087#discussion_r4308108
      use === 'on-dark' ? 'fill-tertiary-pressed' : 'text-core-default',
      children: getCharacterCounterOpacity({
        focused,
        maxLength,
        value
      }) === 1 && /*#__PURE__*/_jsx(ScreenReaderOnly, {
        id: ariaLiveRegionId,
        "aria-live": "polite",
        "aria-atomic": true,
        children: unescapedText('ui.autosizedTextInput.charactersRemaining', {
          value: charactersRemaining
        })
      })
    })]
  });
};
let uuid = 0;
export const AutosizedTextInputComponent = /*#__PURE__*/forwardRef((props, ref) => {
  const [inputWidth, setInputWidth] = useState(props.minWidth);
  const _getStyle = useMemo(() => memoizeOne(getStyle), []);
  const _ariaLiveRegionId = useRef(`autosizedtextinput-maxlength-warning-${uuid++}`);
  const prevInputWidthRef = useRef(inputWidth);
  const measureWidth = () => {
    const {
      inputRef,
      minWidth
    } = props;
    if (!inputRef || !inputRef.current) return;
    const newInputWidth = Math.ceil(Math.max(minWidth || 0, getTextWidth(inputRef.current, null)));
    if (inputWidth !== newInputWidth) {
      setInputWidth(newInputWidth);
    }
  };
  useEffect(() => {
    measureWidth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    // This condition prevents infinite loops
    if (prevInputWidthRef.current === inputWidth) {
      measureWidth();
    }
    prevInputWidthRef.current = inputWidth;
  });
  const handleClick = evt => {
    const {
      inputRef,
      onClick
    } = props;
    callIfPossible(onClick, evt);
    if (!evt.preventPrevented) {
      if (inputRef && inputRef.current) inputRef.current.focus();
    }
  };
  const {
      'aria-describedby': ariaDescribedBy,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      affordance = false,
      error,
      inputRef,
      readOnly,
      use = 'default',
      maxLength,
      value,
      placeholder,
      onChange,
      onKeyDown,
      onKeyUp,
      disabled,
      autoFocus,
      'data-test-id': dataTestId
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  return /*#__PURE__*/_jsx(HoverProvider, Object.assign({}, props, {
    children: hoverProviderProps => /*#__PURE__*/_jsx(FocusProvider, Object.assign({}, props, {
      children: focusProviderProps => {
        const {
            hovered
          } = hoverProviderProps,
          hoverProviderRestProps = _objectWithoutPropertiesLoose(hoverProviderProps, _excluded2);
        const {
            focused
          } = focusProviderProps,
          focusProviderRestProps = _objectWithoutPropertiesLoose(focusProviderProps, _excluded3);
        const hasIcon = affordance && !readOnly;
        return /*#__PURE__*/_jsxs(Wrapper, Object.assign({}, domProps, {
          $cssStyles: cssStyles
        }, hoverProviderRestProps, focusProviderRestProps, {
          focused: focused,
          hovered: hovered,
          onClick: handleClick,
          use: use,
          children: [/*#__PURE__*/_jsx(Affordance, {
            error: error,
            focused: focused,
            hovered: hovered,
            show: affordance,
            use: use,
            children: /*#__PURE__*/_jsx(Input, {
              onChange: onChange,
              onKeyDown: onKeyDown,
              onKeyUp: onKeyUp,
              "aria-invalid": error,
              "aria-describedby": `${ariaDescribedBy} ${_ariaLiveRegionId.current}`,
              "aria-label": ariaLabel,
              "aria-labelledby": ariaLabelledBy,
              value: value,
              maxLength: maxLength,
              affordance: affordance,
              focused: focused,
              hovered: hovered,
              readOnly: readOnly,
              autoFocus: autoFocus,
              ref: inputRef || ref,
              $width: _getStyle(inputWidth),
              use: use,
              placeholder: placeholder,
              disabled: disabled,
              "data-test-id": dataTestId
            })
          }), hasIcon && /*#__PURE__*/_jsx(CharacterCounter, {
            value: value,
            maxLength: maxLength,
            use: use,
            focused: focused,
            ariaLiveRegionId: _ariaLiveRegionId.current
          }), hasIcon && /*#__PURE__*/_jsx(StyledIconWrapper, {
            use: use,
            focused: focused,
            hovered: hovered,
            children: /*#__PURE__*/_jsx(EditIcon, {})
          })]
        }));
      }
    }))
  }));
});
export const AutoSizedTextInput = ShareInput(Controllable( /*#__PURE__*/memo(AutosizedTextInputComponent)));
AutoSizedTextInput.displayName = 'AutoSizedTextInput';